import React, { Suspense, useEffect, useMemo } from 'react';
import { useClientRoutesContext } from '../../../config/clientRoutes/hooks';
import { Loading, PrivatePageWrapper } from '../../shared/components';
import { getPrivateRoutes } from '../routes';
import Routes from './Routes';
import LoggedInFallBackPage from './LoggedInFallBackPage';
import AppBar from './AppBar';
import AppRoot from './AppRoot';
import { IndicatorsModeProvider } from '../../shared/context';
import { PrintHeader } from './Print';
import { useUser } from '../../shared';
import { hotjar } from 'react-hotjar';
import { LogActionTypes, useDataLogging } from '../../shared/data-logging';
import { SliderPanel } from './SliderPanel/SliderPanel';
import { DebugToolbar } from 'src/modules/shared/components/DebugToolbar';
import { useGetChatIdentifyTokenMutation } from 'src/modules/shared/slices/hubspot/api';
import Cookies from 'js-cookie';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { setTargetURI } from 'src/modules/shared/slices/auth/entity/AuthSlice';
import SurveyController from './Survey/SurveyController';

function onConversationsAPIReady() {
  console.log(`HubSpot Conversations API: ${window.HubSpotConversations}`);
}

var _hsq = (window._hsq = window._hsq || []);
const env = process.env.REACT_APP_ENVIRONMENT;
const hubspotScript = process.env.REACT_APP_HUBSPOT_ID;

const LoggedInApp = () => {
  const theme = useTheme();
  const routes = useClientRoutesContext();
  const privateRoutes = getPrivateRoutes(routes);
  const { isTeacher } = useUser();
  const log = useDataLogging();
  const dispatch = useAppDispatch();
  const { isDeveloper } = useUser();
  const [getToken, tokenResponse] = useGetChatIdentifyTokenMutation();
  const mobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const targetURI = useAppSelector((state) => state.auth.targetURI);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    log(LogActionTypes.OPEN_WEBSITE);
  }, [log]);

  if (process.env.NODE_ENV !== 'development' && isTeacher) {
    hotjar.initialize(1746145, 6);
  }

  useEffect(() => {
    if (!targetURI) return;
    if (targetURI) {
      dispatch(setTargetURI(null));
      console.log('logged in redirect to ', targetURI);
      window.location.href = targetURI;
    }
  }, [dispatch, targetURI]);

  useEffect(() => {
    if (!isTeacher) return;
    getToken();
  }, [getToken, isTeacher]);

  const hubSpotCookieId = useMemo(() => {
    return Cookies.get('hubspotutk');
  }, []);

  useEffect(() => {
    if (!isTeacher && hubSpotCookieId !== undefined && env !== 'demo') {
      Cookies.remove('hubspotutk');
      Cookies.remove('hubspotutk', { path: '/', domain: '.lernova.be' });
    }
  }, [hubSpotCookieId, isTeacher]);

  useEffect(() => {
    if (!tokenResponse.isSuccess) return;
    window.hsConversationsSettings = {
      identificationEmail: user?.email,
      identificationToken: tokenResponse.data.token,
    };
    const timer = setTimeout(() => {
      window?.HubSpotConversations?.widget.refresh();
      window.hsConversationsOnReady = [onConversationsAPIReady];
      window?.HubSpotConversations?.widget.load();
    }, 1000);
    return () => clearTimeout(timer);
  }, [tokenResponse, user?.email]);

  useEffect(() => {
    if (!isTeacher) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.referrerPolicy = 'no-referrer unsafe-url';
    script.src = hubspotScript as string;
    document.body.appendChild(script);
  }, [isTeacher]);

  useEffect(() => {
    if (!isTeacher) return;
    _hsq.push([
      'identify',
      {
        email: user?.email,
      },
    ]);
    _hsq.push(['setPath', window.location.pathname]);
    _hsq.push(['trackPageView']);
    _hsq.push(['refreshPageHandlers']);
  }, [isTeacher, user?.email]);

  const checkScript = useMemo(() => {
    return document.getElementById('hs-script-loader');
  }, []);

  useEffect(() => {
    if (!isTeacher && checkScript && checkScript?.parentNode && env !== 'demo') {
      checkScript.parentNode.removeChild(checkScript);
    }
  }, [checkScript, isTeacher]);

  return (
    <AppRoot
      className={user ? 'logged-in' : 'logged-out'}
      presentationmodeon={showPresentationMode}
    >
      <IndicatorsModeProvider>
        <PrintHeader />
        <AppBar />
        <PrivatePageWrapper>
          <Suspense fallback={<Loading />}>
            <Routes routes={privateRoutes} fallBackPage={() => <LoggedInFallBackPage />} />
          </Suspense>
        </PrivatePageWrapper>
        <Suspense fallback={<Loading />}>
          <SliderPanel />
        </Suspense>
      </IndicatorsModeProvider>
      {isDeveloper && !mobile && <DebugToolbar />}
      <SurveyController />
    </AppRoot>
  );
};

export default LoggedInApp;
